import { useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '../components';
import { ThreadConversation } from './ThreadConversation';
import { ThreadStatusBadge } from './ThreadStatusBadge';
import { isFeedbackKind, type Thread } from './types';
import type { ThreadApi } from './threadApi';

export interface ThreadMessagesPanelProps {
    /** The loaded thread to display. */
    thread: Thread;
    /** API used for mutations (reply, edit, status, markSeen). */
    threadApi: ThreadApi;
    /** Current user id — used for unread detection and to know which messages are editable. */
    currentUserId?: string;
    /** Current user display name — used as creator label when current user IS the creator. */
    currentUserName?: string;
    /** Override the displayed creator (description author) name. */
    creatorName?: string;

    /** Whether the current user can post replies. Default true. */
    canReply?: boolean;
    /** Whether the current user can change thread status. Default true. */
    canChangeStatus?: boolean;
    /** Show the status badge above the conversation. Default false. */
    showStatusBadge?: boolean;
    /**
     * Which status actions to expose. Defaults to ['resolve', 'reopen'] (teacher/admin pattern).
     * Pass ['close', 'reopen'] for the student "close my doubt" pattern, or any combination.
     * Each action is only rendered when applicable for the current thread status.
     */
    statusActions?: Array<'resolve' | 'close' | 'reopen'>;
    /** Reply input placeholder. */
    replyPlaceholder?: string;
    /** Size of the Reply button. Default 'md'. Use 'sm' inside dialogs/modals. */
    replyButtonSize?: 'sm' | 'md' | 'lg';
    /** Mark the thread as seen on mount (fire-and-forget). */
    markSeenOnMount?: boolean;

    /** Called whenever the thread changes (reply, edit, status). Lets the parent keep its copy in sync. */
    onThreadChanged?: (thread: Thread) => void;
    /** Extra query keys to invalidate on mutation success (e.g. unread-count keys). */
    invalidateOnChange?: readonly (readonly unknown[])[];
}

/**
 * Bundles the thread conversation rendering with reply / edit / status mutations.
 * Use this anywhere a thread's messages need to be shown with full interactivity
 * (HelpQueriesView detail panel, student question-solving area, etc.).
 */
export function ThreadMessagesPanel({
    thread,
    threadApi,
    currentUserId,
    currentUserName,
    creatorName,
    canReply = true,
    canChangeStatus = true,
    showStatusBadge = false,
    statusActions = ['resolve', 'reopen'],
    replyPlaceholder = 'Write your reply...',
    replyButtonSize = 'md',
    markSeenOnMount = false,
    onThreadChanged,
    invalidateOnChange = [],
}: ThreadMessagesPanelProps) {
    const queryClient = useQueryClient();

    const invalidate = () => {
        invalidateOnChange.forEach(k => queryClient.invalidateQueries({ queryKey: k }));
    };

    const replyMutation = useMutation({
        mutationFn: ({ text }: { text: string }) =>
            threadApi.addMessage(thread.id, thread.ownerId, text),
        onSuccess: (updated) => {
            onThreadChanged?.(updated);
            invalidate();
        },
    });

    const statusMutation = useMutation({
        mutationFn: ({ status }: { status: string }) =>
            threadApi.update(thread.id, thread.ownerId, { status }),
        onSuccess: (updated) => {
            onThreadChanged?.(updated);
            invalidate();
        },
    });

    useEffect(() => {
        if (!markSeenOnMount) return;
        threadApi.update(thread.id, thread.ownerId, { markSeen: true })
            .then((updated) => { onThreadChanged?.(updated); invalidate(); })
            .catch(() => { /* ignore */ });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [thread.id]);

    const locked = thread.status === 'resolved' || thread.status === 'closed';

    const resolvedCreatorName =
        creatorName
        || (currentUserId && thread.createdBy === currentUserId ? currentUserName : undefined)
        || thread.messages.find(m => m.senderId === thread.createdBy)?.senderName
        || thread.messages[0]?.senderName;

    const statusActionButtons = canChangeStatus ? (
        <>
            {statusActions.includes('resolve') && thread.status !== 'resolved' && thread.status !== 'closed' && (
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => statusMutation.mutate({ status: 'resolved' })}
                    loading={statusMutation.isPending}
                >
                    Mark Resolved
                </Button>
            )}
            {statusActions.includes('close') && thread.status !== 'closed' && thread.status !== 'resolved' && (
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => statusMutation.mutate({ status: 'closed' })}
                    loading={statusMutation.isPending}
                >
                    {isFeedbackKind(thread.kind) ? 'Close' : 'Close Doubt'}
                </Button>
            )}
            {statusActions.includes('reopen') && (thread.status === 'resolved' || thread.status === 'closed') && (
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => statusMutation.mutate({ status: 'open' })}
                    loading={statusMutation.isPending}
                >
                    Reopen
                </Button>
            )}
        </>
    ) : null;

    return (
        <div className="hq-msg-panel">
            {showStatusBadge && (
                <div className="hq-msg-panel-status-row">
                    <ThreadStatusBadge status={thread.status} />
                </div>
            )}

            <ThreadConversation
                description={thread.description}
                creatorName={resolvedCreatorName}
                createdAt={thread.createdAt}
                messages={thread.messages}
                onSendMessage={async (text) => {
                    await replyMutation.mutateAsync({ text });
                }}
                canReply={canReply && !locked}
                replyPlaceholder={replyPlaceholder}
                currentUserId={currentUserId}
                status={thread.status}
                onEditMessage={canReply ? async (messageId, text) => {
                    const updated = await threadApi.editMessage(thread.id, messageId, thread.ownerId, text);
                    onThreadChanged?.(updated);
                    invalidate();
                } : undefined}
                replyExtraActions={statusActionButtons}
                showActionsWhenNotReplying
                replyButtonSize={replyButtonSize}
            />
        </div>
    );
}
